"use client";

import Link from "next/link";

import type { PanchangResponse, PanchangPlanet } from "@/lib/api/panchang";
import type { CityData } from "@/lib/cities-data.types";
import { engineEndClock } from "@/lib/local-clock";
import { PANCHANG_CALCULATION_COPY, PANCHANG_CARD_COPY, RASHI_HINDI } from "./Panchang.copy";
import { displayPakshaName, displayTithiName } from "@/lib/tithi-names-hi";
import { nakshatraNameHi } from "@/lib/vedic-names-hi";
import { NakshatraLabel, PlanetLabel, RashiLabel, RashiPositionLabel } from "./Panchang.labels";

const PLANET_LABELS: Record<string, { en: string; hi: string; symbol: string }> = {
  sun: { en: "Sun", hi: "सूर्य", symbol: "☉" },
  moon: { en: "Moon", hi: "चंद्र", symbol: "☽" },
  mars: { en: "Mars", hi: "मंगल", symbol: "♂" },
  mercury: { en: "Mercury", hi: "बुध", symbol: "☿" },
  jupiter: { en: "Jupiter", hi: "गुरु", symbol: "♃" },
  venus: { en: "Venus", hi: "शुक्र", symbol: "♀" },
  saturn: { en: "Saturn", hi: "शनि", symbol: "♄" },
  rahu: { en: "Rahu", hi: "राहु", symbol: "☊" },
  ketu: { en: "Ketu", hi: "केतु", symbol: "☋" },
};

function NativeDisclosure({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details className="group" open={defaultOpen}>
      <summary className="flex cursor-pointer list-none items-center justify-between rounded-lg border border-ish-border bg-ish-bg-card px-4 py-3 text-body font-semibold text-ish-dark">
        {title}
        <span className="text-[18px] leading-none text-intent-brand transition-transform group-open:rotate-45">
          +
        </span>
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

function SkeletonBlock({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-md bg-stone-200/70 ${className}`} aria-hidden="true" />
  );
}

export function PanchangResultSkeleton({ isHindi = false }: { isHindi?: boolean }) {
  return (
    <div className="space-y-6" aria-label={PANCHANG_CARD_COPY[isHindi ? "hi" : "en"].loading}>
      <div className="card-sacred p-6 sm:p-8">
        <div className="mb-6 text-center sm:text-left">
          <span className="block font-serif text-[32px] font-bold leading-tight text-ish-dark sm:text-[40px]">
            Today&apos;s Panchang
          </span>
          <span className="mt-2 block text-body text-ish-muted">
            Calculating today&apos;s tithi, nakshatra and muhurat
          </span>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {[0, 1].map((item) => (
            <div key={item} className="text-center sm:text-left">
              <SkeletonBlock className="mx-auto mb-3 h-3 w-20 sm:mx-0" />
              <SkeletonBlock className="mx-auto h-8 w-36 sm:mx-0" />
              <SkeletonBlock className="mx-auto mt-2 h-3 w-44 sm:mx-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="card-sacred p-4 text-center">
            <SkeletonBlock className="mx-auto h-3 w-20" />
            <SkeletonBlock className="mx-auto mt-3 h-5 w-16" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[0, 1].map((item) => (
          <div key={item} className="card-sacred p-4 text-center">
            <SkeletonBlock className="mx-auto h-3 w-16" />
            <SkeletonBlock className="mx-auto mt-3 h-5 w-24" />
          </div>
        ))}
      </div>

      <div className="card-sacred p-6 sm:p-8">
        <SkeletonBlock className="mb-5 h-6 w-44" />
        <div className="space-y-2">
          {[0, 1, 2, 3].map((item) => (
            <SkeletonBlock key={item} className="h-12" />
          ))}
        </div>
      </div>

      <div>
        <SkeletonBlock className="h-12 w-full" />
        <div className="card-sacred mt-3 space-y-2 p-6 sm:p-8">
          {[0, 1, 2, 3, 4, 5, 6].map((item) => (
            <SkeletonBlock key={item} className="h-14" />
          ))}
        </div>
      </div>

      <SkeletonBlock className="mx-auto h-11 w-52" />
    </div>
  );
}

// ---------------------------------------------------------------------------------

type CardProps = { result: PanchangResponse; isHindi?: boolean };

function cardCopy(isHindi: boolean) {
  return PANCHANG_CARD_COPY[isHindi ? "hi" : "en"];
}

/** Hindi rashi name for a Hindi page; undefined keeps the label's Latin default. */
function rashiLabel(rashiId: string, isHindi: boolean): string | undefined {
  return isHindi ? RASHI_HINDI[rashiId] : undefined;
}

export function TithiNakshatraCard({ result, isHindi = false }: CardProps) {
  const copy = cardCopy(isHindi);
  const language = isHindi ? "hi" : "en";
  return (
    <div className="card-sacred p-6 sm:p-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <dl className="text-center sm:text-left">
          <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-2 text-ish-faint">
            {copy.tithi}
          </dt>
          <dd>
            <span className="block text-[22px] font-bold text-ish-dark font-serif">
              {displayTithiName(result.tithi.name, language)}
            </span>
            <span className="block text-body-small mt-1 text-ish-muted">
              {copy.pakshaLine(
                displayPakshaName(result.tithi.paksha, language),
                result.tithi.number
              )}
            </span>
            {result.tithi.end_time ? (
              <span className="block text-caption mt-0.5 text-ish-faint">
                {copy.upto(engineEndClock(result.tithi, result.date, language))}
              </span>
            ) : null}
          </dd>
        </dl>
        <dl className="text-center sm:text-left">
          <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-2 text-ish-faint">
            {copy.nakshatra}
          </dt>
          <dd>
            <span className="block">
              <NakshatraLabel
                nakshatraId={result.moon_nakshatra}
                label={(isHindi && nakshatraNameHi(result.moon_nakshatra)) || undefined}
              />
            </span>
            {/* The end time is the nakshatra's, so it sits under the nakshatra
                name: after the Moon-sign line it read as the sign's end time. */}
            {result.nakshatra.end_time ? (
              <span className="block text-caption mt-0.5 text-ish-faint">
                {copy.upto(engineEndClock(result.nakshatra, result.date, language))}
              </span>
            ) : null}
            <MoonRashiLine result={result} isHindi={isHindi} />
          </dd>
        </dl>
      </div>
    </div>
  );
}

function MoonRashiLine({ result, isHindi }: { result: PanchangResponse; isHindi: boolean }) {
  const rashi = (
    <RashiLabel
      rashiId={result.moon_rashi}
      label={rashiLabel(result.moon_rashi, isHindi)}
      className="text-body-small capitalize text-ish-muted font-medium"
    />
  );
  return (
    <span className="block text-body-small mt-1 capitalize text-ish-muted">
      {isHindi ? (
        <>चंद्रमा {rashi} राशि में</>
      ) : (
        <>
          Moon in {rashi} ({RASHI_HINDI[result.moon_rashi] ?? ""})
        </>
      )}
    </span>
  );
}

export function QuickInfoGrid({ result, isHindi = false }: CardProps) {
  const copy = cardCopy(isHindi);
  const rashis: ReadonlyArray<readonly [string, string]> = [
    [copy.moonRashi, result.moon_rashi],
    [copy.sunRashi, result.sun_rashi],
    [copy.lagna, result.lagna_rashi],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {rashis.map(([label, rashiId]) => (
        <dl key={label} className="card-sacred p-4 text-center">
          <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
            {label}
          </dt>
          <dd>
            <RashiLabel rashiId={rashiId} label={rashiLabel(rashiId, isHindi)} />
          </dd>
        </dl>
      ))}
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          {copy.paksha}
        </dt>
        <dd className="text-body font-bold capitalize text-ish-dark">
          {displayPakshaName(result.tithi.paksha, isHindi ? "hi" : "en")}
        </dd>
      </dl>
    </div>
  );
}

export function YogaKaranaRow({ result, isHindi = false }: CardProps) {
  const copy = cardCopy(isHindi);
  return (
    <div className="grid grid-cols-2 gap-3">
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          {copy.yoga}
        </dt>
        <dd>
          <span className="text-body font-bold text-ish-dark">{result.yoga.name}</span>
          {result.yoga.end_time ? (
            <span className="block text-[11px] mt-0.5 text-ish-faint">
              {copy.upto(engineEndClock(result.yoga, result.date, isHindi ? "hi" : "en"))}
            </span>
          ) : null}
        </dd>
      </dl>
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          {copy.karana}
        </dt>
        <dd className="text-body font-bold text-ish-dark">{result.karana.name}</dd>
      </dl>
    </div>
  );
}

export function SunMoonTimingsCard({ result, isHindi = false }: CardProps) {
  const copy = cardCopy(isHindi);
  const items: ReadonlyArray<readonly [string, string | null | undefined]> = [
    [copy.sunrise, result.sunrise],
    [copy.sunset, result.sunset],
    [copy.moonrise, result.moonrise],
    [copy.moonset, result.moonset],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {items.map(([label, value]) => (
        <dl key={label} className="card-sacred p-4 text-center">
          <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
            {label}
          </dt>
          <dd className="text-body font-bold text-ish-dark">{value ?? "—"}</dd>
        </dl>
      ))}
    </div>
  );
}

/**
 * How much of the method a panchang surface explains inline.
 *
 * `full` is the hub's explainer. `summary` is for the ~400 city pages, where the
 * five method paragraphs rendered verbatim on every sibling and repeated what the
 * city FAQ said: those keep their own place/date/zone/coordinate facts, the
 * sunrise basis and ONE method sentence that links to /methodology/.
 */
export type PanchangCalculationDetail = "full" | "summary";

export function PanchangCalculationDisclosure({
  result,
  city,
  isHindi,
  detail = "full",
}: {
  result: Pick<PanchangResponse, "date" | "location">;
  city: CityData;
  isHindi: boolean;
  detail?: PanchangCalculationDetail;
}) {
  const copy = PANCHANG_CALCULATION_COPY[isHindi ? "hi" : "en"];
  const facts = [
    [copy.place, isHindi ? (city.name_hi ?? city.name) : city.name],
    [copy.date, result.date],
    [copy.timezone, city.timezone],
    [copy.coordinates, `${result.location.latitude}, ${result.location.longitude}`],
  ];
  const explanations: readonly (readonly [string, string])[] = [
    [copy.tithi, copy.tithiExplanation],
    [copy.nakshatra, copy.nakshatraExplanation],
    [copy.method, copy.methodExplanation],
    [copy.tradition, copy.traditionExplanation],
    [copy.limits, copy.limitsExplanation],
  ];

  return (
    <NativeDisclosure title={copy.title}>
      <div className="card-sacred space-y-5 p-5 sm:p-6 text-body-small text-ish-muted">
        <dl className="space-y-3">
          {facts.map(([label, value]) => (
            <div key={label} className="grid gap-1 sm:grid-cols-2 sm:gap-4">
              <dt>{label}</dt>
              <dd className="break-words font-semibold text-ish-dark">{value}</dd>
            </div>
          ))}
        </dl>
        <p>{copy.basis}</p>
        {detail === "summary" ? (
          <p>
            {copy.summaryMethod}{" "}
            <Link
              href="/methodology/"
              className="font-semibold text-intent-brand underline underline-offset-4"
            >
              {copy.summaryMethodLink}
            </Link>
          </p>
        ) : (
          <PanchangMethodExplanations copy={copy} explanations={explanations} />
        )}
      </div>
    </NativeDisclosure>
  );
}

function PanchangMethodExplanations({
  copy,
  explanations,
}: {
  copy: (typeof PANCHANG_CALCULATION_COPY)["en" | "hi"];
  explanations: readonly (readonly [string, string])[];
}) {
  return (
    <>
      <p>{copy.locationExplanation}</p>
      {explanations.map(([title, explanation]) => (
        <div key={title}>
          <h2 className="mb-1 font-serif text-body font-semibold text-ish-dark">{title}</h2>
          <p>{explanation}</p>
        </div>
      ))}
      <a
        href="https://www.astro.com/swisseph/swephprg.htm"
        className="inline-block py-2 font-semibold text-intent-brand underline underline-offset-4"
      >
        {copy.reference}
      </a>
    </>
  );
}

const PERIOD_COPY = {
  en: {
    heading: "Auspicious & Inauspicious Periods",
    rahuKaal: "Rahu Kaal",
    yamaghanta: "Yamaghanta",
    gulikaKaal: "Gulika Kaal",
    brahmaMuhurat: "Brahma Muhurat",
    amritKaal: "Amrit Kaal",
    abhijitMuhurta: "Abhijit Muhurta",
  },
  hi: {
    heading: "शुभ और अशुभ काल",
    rahuKaal: "राहु काल",
    yamaghanta: "यमगंड काल",
    gulikaKaal: "गुलिक काल",
    brahmaMuhurat: "ब्रह्म मुहूर्त",
    amritKaal: "अमृत काल",
    abhijitMuhurta: "अभिजित मुहूर्त",
  },
} as const;

export function InauspiciousPeriodsCard({
  result,
  isHindi = false,
}: {
  result: PanchangResponse;
  isHindi?: boolean;
}) {
  const copy = PERIOD_COPY[isHindi ? "hi" : "en"];
  const periods = [
    { label: copy.rahuKaal, period: result.rahu_kaal },
    { label: copy.yamaghanta, period: result.yamaghanta },
    { label: copy.gulikaKaal, period: result.gulika_kaal },
  ];
  return (
    <div className="card-sacred p-6 sm:p-8">
      <h2 className="text-[18px] font-bold mb-4 text-ish-dark font-serif">{copy.heading}</h2>
      <dl className="space-y-2">
        {periods.map(({ label, period }) => (
          <div
            key={label}
            className="flex items-center justify-between p-3 rounded-lg bg-error/4 border border-error/12"
          >
            <dt className="text-body font-semibold text-ish-dark">{label}</dt>
            <dd className="text-body-small text-ish-muted">
              {period.start} – {period.end}
            </dd>
          </div>
        ))}
        {result.brahma_muhurat ? (
          <div className="flex items-center justify-between p-3 rounded-lg bg-success/4 border border-success/15">
            <dt className="text-body font-semibold text-ish-dark">{copy.brahmaMuhurat} ✓</dt>
            <dd className="text-body-small text-ish-muted">
              {result.brahma_muhurat.start} – {result.brahma_muhurat.end}
            </dd>
          </div>
        ) : null}
        {result.amrit_kaal ? (
          <div className="flex items-center justify-between p-3 rounded-lg bg-success/4 border border-success/15">
            <dt className="text-body font-semibold text-ish-dark">{copy.amritKaal} ✓</dt>
            <dd className="text-body-small text-ish-muted">
              {result.amrit_kaal.start} – {result.amrit_kaal.end}
            </dd>
          </div>
        ) : null}
        <div className="flex items-center justify-between p-3 rounded-lg bg-success/4 border border-success/15">
          <dt className="text-body font-semibold text-ish-dark">{copy.abhijitMuhurta} ✓</dt>
          <dd className="text-body-small text-ish-muted">
            {result.abhijit_muhurta.start} – {result.abhijit_muhurta.end}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function PlanetRow({ planet, isHindi }: { planet: PanchangPlanet; isHindi: boolean }) {
  const label = PLANET_LABELS[planet.planet_id];
  if (!label) return null;
  const copy = cardCopy(isHindi);
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-black/2 border border-ish-border-light">
      <PlanetLabel
        planetId={planet.planet_id}
        symbol={label.symbol}
        labelEn={isHindi ? label.hi : label.en}
        labelHi={isHindi ? label.en : label.hi}
      />
      <div className="text-right">
        <RashiPositionLabel rashiId={planet.rashi} label={rashiLabel(planet.rashi, isHindi)} />
        <span className="text-caption ml-1.5 text-ish-faint">
          {planet.rashi_degree.toFixed(1)}&deg;
        </span>
        {planet.is_retrograde && (
          <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-error/10 text-error">
            {copy.retrograde}
          </span>
        )}
      </div>
    </div>
  );
}

export function PlanetPositionsDisclosure({ result, isHindi = false }: CardProps) {
  return (
    <NativeDisclosure title={cardCopy(isHindi).planets} defaultOpen>
      <div className="card-sacred p-6 sm:p-8 mt-0 space-y-2">
        {result.planets
          .filter((p) => PLANET_LABELS[p.planet_id])
          .map((planet: PanchangPlanet) => (
            <PlanetRow key={planet.planet_id} planet={planet} isHindi={isHindi} />
          ))}
      </div>
    </NativeDisclosure>
  );
}
